import { Observable, throwError } from 'rxjs'
import { catchError, map, share } from 'rxjs/operators'

import config from '../lib/config'
import { LocationService } from '../lib/location'
import { RequestConfig, SeBarRequest } from '../lib/request'

import {
  AuthenticatedGlobalSessionResponse,
  AuthenticatedGlobalSessionState,
  mapAuthenticatedResponseToState,
} from './global-session-state'
import { UnauthorizedGlobalSessionResponseError } from './invalid-global-session-response-error'

const GLOBAL_SESSION_PATH = 'global_session/me'

function traceIdFromSessionError(err: any): string {
  return err instanceof UnauthorizedGlobalSessionResponseError ? err.traceId : err.response?.data?.metadata?.trace_id
}

export class GlobalSessionEndpoint {

  private get primaryAPIBaseUrl(): string {
    return config.urls.seApi
  }

  private get alternateAPIBaseUrl(): string {
    if (this.location.isVanityDomain) {
      return `https://${this.location.origin}/apigateway`
    }
    return config.urls.apiProxy
  }

  constructor(private location: LocationService, private request: SeBarRequest) {}

  /**
   * Attempts to fetch the current "global session" object for the current user from the SE API.
   *
   * If a {@link GlobalSessionEndpoint} call has not previously resulting a 200 response from the API, it will attempt
   * to "discover" a working base URL using {@link alternateAPIBaseUrl}.
   */
  public fetch(): Observable<AuthenticatedGlobalSessionState> {
    return this.doRequest().pipe(
      map(mapAuthenticatedResponseToState),
      share(),
    )
  }

  private doRequest(): Observable<AuthenticatedGlobalSessionResponse> {
    if (this.location.isVanityDomain && !config.loggedIn) {
      // On an ngin site but not logged into site
      return throwError(new UnauthorizedGlobalSessionResponseError('auto'))
    }

    const request$ = this.doRequestWithRetries()

    return request$.pipe(
      catchError(err => {
        console.log('[se-bar] GlobalSessionEndpoint.doRequest.  Interpreting request errors as unauthenticated', err)
        return throwError(new UnauthorizedGlobalSessionResponseError(traceIdFromSessionError(err)))
      }),
    )
  }

  private doRequestWithRetries(): Observable<AuthenticatedGlobalSessionResponse> {
    return this.doRequestWithBaseUrlDiscovery().pipe(
      catchError((err) => this.doRequestWithBaseUrlDiscovery())
    )
  }

  private doRequestWithBaseUrlDiscovery(): Observable<AuthenticatedGlobalSessionResponse> {
    return this.createRequest(this.primaryAPIBaseUrl).pipe(
      catchError((err) => {
        return this.createRequest(this.alternateAPIBaseUrl)
      })
    )
  }

  private getMakeRequestFn(baseUrl: string): () => Promise<AuthenticatedGlobalSessionResponse> {
    const config: RequestConfig = {
      headers: {
        Accept: 'application/json',
      },
      simpleHeaders: true,
    }
    if (baseUrl) {
      config.baseURL = baseUrl
    }

    /** `as unknown as () => Promise<AuthenticatedGlobalSessionResponse>` because the response gets unwrapped by
     * ../lib/request.ts before callers receive it.
     */
    return (() => this.request.get<AuthenticatedGlobalSessionResponse>(GLOBAL_SESSION_PATH, config)) as unknown as () => Promise<AuthenticatedGlobalSessionResponse>
  }

  private createRequest(baseUrl: string): Observable<AuthenticatedGlobalSessionResponse> {
    const makeRequest = this.getMakeRequestFn(baseUrl)
    /**
     * The Observable constructor is used instead of rxjs's {@link from} when converting from Axios's promise to enable
     * retry logic - resubscribing to an observable created with `from(promise)` would not cause the request to be
     * issued again, as it would still be based on the result of the original promise. Creating the promise in the
     * "subscribe" handler for the Observable allows the request to be reissued by re-subscribing.
     */
    return new Observable<AuthenticatedGlobalSessionResponse>(o => {

      console.log('[se-bar] GlobalSessionEndpoint.createRequest', { baseUrl })
      makeRequest()
        .then(res => {
          const isAuthenticated = !!res?.id

          // if we do get a response but the session is null or empty try again for refresh
          if (!isAuthenticated) {
            console.warn('not authenticated', res)
            return Promise.reject(new UnauthorizedGlobalSessionResponseError(res?.trace_id))
          }

          this.request.setRequestBaseUrl(baseUrl)
          return res
        })
        .then(res => {
          o.next(res)
          o.complete()
        })
        .catch(err => o.error(err))
    })
  }
}
